import React, { useEffect, useMemo, useState } from "react";
import {
  Avatar,
  Box,
  Button,
  Chip,
  CircularProgress,
  IconButton,
  InputAdornment,
  MenuItem,
  TextField,
  Typography,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import ClearIcon from "@mui/icons-material/Clear";
import FileDownloadOutlinedIcon from "@mui/icons-material/FileDownloadOutlined";
import SearchIcon from "@mui/icons-material/Search";
import { Icon } from "@iconify/react";
import roundClearAll from "@iconify/icons-ic/round-clear-all";
import roundFilterList from "@iconify/icons-ic/round-filter-list";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useSnackbar } from "notistack";
import moment from "moment";
import CustomMUITable from "src/components/CustomMUITable";
import CustomConfirmation from "src/components/ModalPopover/Confirmation";
import CustomDrawer from "src/components/DrawerForm/CustomDrawer";
import Label from "src/components/Label";
import { get_root_value } from "src/utils/domUtils";
import { normalizeTracker } from "./trackerStorage";
import { getTrackersApi } from "src/DAL/tracker/tracker";
import {
  deleteSubmissionFromApi,
  getInitials,
  getSubmissionReviewLabel,
  getSubmissionSourceLabel,
  isGymriteMemberSource,
  loadSubmissionsFromApi,
  normalizeSubmissionSource,
  SUBMISSION_REVIEW,
  SUBMISSION_REVIEW_OPTIONS,
  SUBMISSION_SOURCE_OPTIONS,
} from "./submissionStorage";

const PRIMARY = "var(--portal-theme-primary)";
const PRIMARY_SOFT =
  "color-mix(in srgb, var(--portal-theme-primary) 12%, white)";
const BORDER = "#e2e8f0";
const MUTED = "#64748b";

const fieldSx = {
  "& .MuiOutlinedInput-root": {
    borderRadius: "10px",
    bgcolor: "#fff",
  },
};

const StatCard = ({ label, value, hint, active, onClick, tone }) => {
  const tones = {
    default: { border: BORDER, value: "#0f172a" },
    warn: { border: "#fde68a", value: "#b45309" },
    danger: { border: "#fecaca", value: "#b91c1c" },
    primary: { border: PRIMARY, value: PRIMARY },
  };
  const t = tones[tone] || tones.default;
  return (
    <Box
      component={onClick ? "button" : "div"}
      type={onClick ? "button" : undefined}
      onClick={onClick}
      sx={{
        flex: "1 1 140px",
        minWidth: 130,
        p: 1.75,
        borderRadius: "12px",
        border: `1.5px solid ${active ? t.border : BORDER}`,
        bgcolor: active ? "#fff" : "#fff",
        boxShadow: active
          ? "0 2px 10px rgba(15, 23, 42, 0.08)"
          : "0 1px 2px rgba(15, 23, 42, 0.04)",
        textAlign: "left",
        cursor: onClick ? "pointer" : "default",
        transition: "border-color 0.15s ease, box-shadow 0.15s ease",
        "&:hover": onClick
          ? {
              borderColor: t.border,
              boxShadow: "0 2px 10px rgba(15, 23, 42, 0.08)",
            }
          : undefined,
      }}
    >
      <Typography sx={{ fontSize: 11.5, fontWeight: 700, color: MUTED }}>
        {label}
      </Typography>
      <Typography
        sx={{
          mt: 0.45,
          fontSize: 26,
          fontWeight: 750,
          color: t.value,
          lineHeight: 1.1,
        }}
      >
        {value}
      </Typography>
      {hint ? (
        <Typography sx={{ mt: 0.45, fontSize: 11.5, color: MUTED }}>
          {hint}
        </Typography>
      ) : null}
    </Box>
  );
};

const SourceChip = ({ source, isNew }) => (
  <Box sx={{ display: "inline-flex", alignItems: "center", gap: 0.5 }}>
    <Chip
      size="small"
      label={getSubmissionSourceLabel(source)}
      sx={{
        height: 22,
        fontSize: 10.5,
        fontWeight: 700,
        bgcolor: isGymriteMemberSource(source) ? PRIMARY_SOFT : "#f1f5f9",
        color: isGymriteMemberSource(source) ? PRIMARY : "#475569",
        border: `1px solid ${
          isGymriteMemberSource(source) ? "transparent" : BORDER
        }`,
        "& .MuiChip-label": { px: 0.85 },
      }}
    />
    {isNew ? (
      <Chip
        size="small"
        label="New"
        sx={{
          height: 22,
          fontSize: 10,
          fontWeight: 800,
          bgcolor: "#ecfdf5",
          color: "#047857",
          border: "1px solid #a7f3d0",
          "& .MuiChip-label": { px: 0.75 },
        }}
      />
    ) : null}
  </Box>
);

const ReviewCell = ({ status, detail }) => {
  if (!status || status === SUBMISSION_REVIEW.NONE) {
    return (
      <Typography sx={{ fontSize: 12.5, color: MUTED }}>—</Typography>
    );
  }
  const color =
    status === SUBMISSION_REVIEW.OVERDUE
      ? "error"
      : status === SUBMISSION_REVIEW.PENDING
        ? "warning"
        : "success";
  return (
    <Box>
      <Label variant="ghost" color={color}>
        {getSubmissionReviewLabel(status)}
      </Label>
      {/* {detail ? (
        <Typography
          sx={{
            mt: 0.4,
            fontSize: 11.5,
            color: MUTED,
            lineHeight: 1.35,
            maxWidth: 140,
          }}
          title={detail}
        >
          {detail}
        </Typography>
      ) : null} */}
    </Box>
  );
};

export default function TrackerSubmissionsList() {
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const [searchParams, setSearchParams] = useSearchParams();
  const [rows, setRows] = useState([]);
  const [trackers, setTrackers] = useState([]);
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);
  const [openDelete, setOpenDelete] = useState(false);
  const [deleteDoc, setDeleteDoc] = useState(null);
  // Right-side portal filter drawer (CustomDrawer) — not an inline panel.
  const [filterDrawerOpen, setFilterDrawerOpen] = useState(false);
  const [drawerFilters, setDrawerFilters] = useState({
    source: "all",
    tag: "all",
    coach: "all",
  });

  const trackerFilter = searchParams.get("tracker") || "all";
  const reviewFilter = searchParams.get("review") || "all";
  const statusFilter = searchParams.get("status") || "all";
  const tagFilter = searchParams.get("tag") || "all";
  const coachFilter = searchParams.get("coach") || "all";
  const sourceFilter = searchParams.get("source") || "all";

  const moreFilterCount = [
    sourceFilter !== "all",
    tagFilter !== "all",
    coachFilter !== "all",
  ].filter(Boolean).length;

  const openFilterDrawer = () => {
    setDrawerFilters({
      source: sourceFilter,
      tag: tagFilter,
      coach: coachFilter,
    });
    setFilterDrawerOpen(true);
  };

  const closeFilterDrawer = () => setFilterDrawerOpen(false);

  const applyDrawerFilters = () => {
    const next = new URLSearchParams(searchParams);
    ["source", "tag", "coach"].forEach((key) => {
      const value = drawerFilters[key];
      if (!value || value === "all") next.delete(key);
      else next.set(key, value);
    });
    setSearchParams(next);
    setFilterDrawerOpen(false);
  };

  const clearDrawerFilters = () => {
    setDrawerFilters({ source: "all", tag: "all", coach: "all" });
    const next = new URLSearchParams(searchParams);
    next.delete("source");
    next.delete("tag");
    next.delete("coach");
    setSearchParams(next);
  };

  const load = async () => {
    setLoading(true);
    try {
      const trackerId = trackerFilter !== "all" ? trackerFilter : null;
      const [subsResult, trackersRes] = await Promise.all([
        loadSubmissionsFromApi(trackerId, { limit: 500 }),
        getTrackersApi({ limit: 200 }),
      ]);

      setRows(subsResult.rows || []);

      if (
        trackersRes?.code === 200 &&
        Array.isArray(trackersRes.trackers)
      ) {
        setTrackers(
          trackersRes.trackers.map(normalizeTracker).filter(Boolean)
        );
      } else {
        setTrackers([]);
      }

      if (!subsResult.ok) {
        enqueueSnackbar(
          subsResult.message || "Failed to load submissions",
          { variant: "error" }
        );
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, [trackerFilter]);

  const selectedTracker = useMemo(
    () => trackers.find((t) => t.id === trackerFilter) || null,
    [trackers, trackerFilter]
  );

  const statusOptions = useMemo(() => {
    if (trackerFilter === "all") return [];
    const names = new Set();
    rows
      .filter((row) => row.trackerId === trackerFilter)
      .forEach((row) => {
        if (row.statusName) names.add(row.statusName);
      });
    return Array.from(names).sort();
  }, [rows, trackerFilter]);

  const tagOptions = useMemo(() => {
    const names = new Set();
    if (selectedTracker?.tags) {
      selectedTracker.tags.forEach((t) => {
        if (t) names.add(String(t));
      });
    }
    rows
      .filter((row) =>
        trackerFilter === "all" ? true : row.trackerId === trackerFilter
      )
      .forEach((row) => {
        (Array.isArray(row.tags) ? row.tags : []).forEach((t) => {
          if (t) names.add(String(t));
        });
      });
    return Array.from(names).sort();
  }, [rows, trackerFilter, selectedTracker]);

  const coachOptions = useMemo(() => {
    const names = new Set();
    rows.forEach((row) => {
      if (row.coachName) names.add(row.coachName);
    });
    return Array.from(names).sort();
  }, [rows]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return rows.filter((row) => {
      if (trackerFilter !== "all" && row.trackerId !== trackerFilter)
        return false;
      if (reviewFilter === "due") {
        if (
          row.reviewStatus !== SUBMISSION_REVIEW.PENDING &&
          row.reviewStatus !== SUBMISSION_REVIEW.OVERDUE
        ) {
          return false;
        }
      } else if (reviewFilter !== "all" && row.reviewStatus !== reviewFilter) {
        return false;
      }
      if (statusFilter !== "all" && row.statusName !== statusFilter)
        return false;
      if (tagFilter !== "all") {
        const tags = Array.isArray(row.tags) ? row.tags : [];
        if (!tags.some((t) => String(t) === tagFilter)) return false;
      }
      if (coachFilter !== "all" && row.coachName !== coachFilter) return false;
      if (
        sourceFilter !== "all" &&
        normalizeSubmissionSource(row.source) !==
          normalizeSubmissionSource(sourceFilter)
      ) {
        return false;
      }
      if (q) {
        const hay =
          `${row.memberName} ${row.email} ${row.formName} ${row.trackerName} ${row.coachName} ${row.statusName} ${(row.tags || []).join(" ")}`.toLowerCase();
        if (!hay.includes(q)) return false;
      }
      return true;
    });
  }, [
    rows,
    trackerFilter,
    reviewFilter,
    statusFilter,
    tagFilter,
    coachFilter,
    sourceFilter,
    search,
  ]);

  const allStats = useMemo(() => {
    const people = new Set(
      rows.map((row) => row.memberId || row.email || row.memberName || row.id)
    );
    const reviewsDue = rows.filter(
      (row) =>
        row.reviewStatus === SUBMISSION_REVIEW.PENDING ||
        row.reviewStatus === SUBMISSION_REVIEW.OVERDUE
    ).length;
    const overdue = rows.filter(
      (row) => row.reviewStatus === SUBMISSION_REVIEW.OVERDUE
    ).length;
    return {
      total: rows.length,
      people: people.size,
      reviewsDue,
      overdue,
    };
  }, [rows]);

  const setFilter = (key, value) => {
    const next = new URLSearchParams(searchParams);
    if (!value || value === "all") next.delete(key);
    else next.set(key, value);
    if (key === "tracker") {
      next.delete("status");
      next.delete("tag");
    }
    setSearchParams(next);
  };

  const clearAllFilters = () => {
    setSearchParams({});
    setSearch("");
  };

  const clearTrackerFilter = () => {
    const next = new URLSearchParams(searchParams);
    next.delete("tracker");
    next.delete("status");
    next.delete("tag");
    setSearchParams(next);
  };

  const activeFilterChips = useMemo(() => {
    const chips = [];
    if (selectedTracker) {
      chips.push({
        key: "tracker",
        label: `Tracker: ${selectedTracker.name || "Untitled"}`,
        onClear: clearTrackerFilter,
      });
    }
    if (reviewFilter !== "all") {
      chips.push({
        key: "review",
        label:
          `Review: ${getSubmissionReviewLabel(reviewFilter)}`,
        onClear: () => setFilter("review", "all"),
      });
    }
    if (statusFilter !== "all") {
      chips.push({
        key: "status",
        label: `Status: ${statusFilter}`,
        onClear: () => setFilter("status", "all"),
      });
    }
    if (tagFilter !== "all") {
      chips.push({
        key: "tag",
        label: `Tag: ${tagFilter}`,
        onClear: () => setFilter("tag", "all"),
      });
    }
    if (coachFilter !== "all") {
      chips.push({
        key: "coach",
        label: `Coach: ${coachFilter}`,
        onClear: () => setFilter("coach", "all"),
      });
    }
    if (sourceFilter !== "all") {
      chips.push({
        key: "source",
        label: `Source: ${getSubmissionSourceLabel(sourceFilter)}`,
        onClear: () => setFilter("source", "all"),
      });
    }
    if (search.trim()) {
      chips.push({
        key: "search",
        label: `Search: ${search.trim()}`,
        onClear: () => setSearch(""),
      });
    }
    return chips;
  }, [
    selectedTracker,
    reviewFilter,
    statusFilter,
    tagFilter,
    coachFilter,
    sourceFilter,
    search,
  ]);

  const handleDelete = async () => {
    if (!deleteDoc?.id) return;
    const result = await deleteSubmissionFromApi(deleteDoc.id);
    setOpenDelete(false);
    setDeleteDoc(null);
    if (result.ok) {
      enqueueSnackbar("Submission deleted", { variant: "success" });
      load();
    } else {
      enqueueSnackbar(result.message || "Delete failed", { variant: "error" });
    }
  };

  const exportCsv = () => {
    const header = [
      "Person",
      "Email",
      "Source",
      "Tracker",
      "Form",
      "Submitted",
      "Status",
      "Review",
      "Coach",
    ];
    const lines = filtered.map((row) =>
      [
        row.memberName,
        row.email,
        getSubmissionSourceLabel(row.source),
        row.trackerName,
        row.formName,
        row.submittedAt
          ? moment(row.submittedAt).format("YYYY-MM-DD HH:mm")
          : "",
        row.statusName,
        getSubmissionReviewLabel(row.reviewStatus),
        row.coachName,
      ]
        .map((cell) => `"${String(cell || "").replace(/"/g, '""')}"`)
        .join(",")
    );
    const blob = new Blob([[header.join(","), ...lines].join("\n")], {
      type: "text/csv;charset=utf-8;",
    });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `tracker-submissions-${moment().format("YYYYMMDD-HHmm")}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const openDetail = (row) => {
    if (!row?.id) return;
    navigate(`/tracker-submissions/${row.id}`);
  };

  const MENU_OPTIONS = [
    {
      label: "View details",
      icon: "akar-icons:eye",
      handleClick: (row) => openDetail(row),
    },
    {
      label: "Open tracker",
      icon: "mdi:clipboard-text-outline",
      handleClick: (row) => {
        if (!row?.trackerId) return;
        navigate(`/trackers/${row.trackerId}`);
      },
    },
    {
      label: "Delete",
      icon: "ant-design:delete-twotone",
      handleClick: (row) => {
        setDeleteDoc(row);
        setOpenDelete(true);
      },
    },
  ];

  const TABLE_HEAD = [
    {
      id: "action",
      label: "Action",
      type: "action",
      MENU_OPTIONS: "MENU_OPTIONS",
    },
    { id: "number", label: "#", type: "number" },
    {
      id: "person",
      label: "Person",
      renderData: (row) => (
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1.25,
            cursor: "pointer",
            py: 0.25,
            maxWidth: 320,
            "&:hover .person-name": { color: PRIMARY },
          }}
          onClick={(e) => {
            e.stopPropagation();
            openDetail(row);
          }}
        >
          <Avatar
            sx={{
              width: 36,
              height: 36,
              fontSize: 12,
              fontWeight: 700,
              bgcolor: PRIMARY_SOFT,
              color: PRIMARY,
              flexShrink: 0,
            }}
          >
            {getInitials(row.memberName)}
          </Avatar>
          <Box sx={{ minWidth: 0 }}>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 0.75,
                minWidth: 0,
                flexWrap: "wrap",
              }}
            >
              <Typography
                className="person-name"
                sx={{
                  fontSize: 13.5,
                  fontWeight: 700,
                  color: "#0f172a",
                  transition: "color 0.15s ease",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                  maxWidth: 160,
                }}
              >
                {row.memberName || "—"}
              </Typography>
              <SourceChip source={row.source} isNew={row.isNew} />
            </Box>
            <Typography
              sx={{
                mt: 0.15,
                fontSize: 12,
                color: MUTED,
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
            >
              {row.email || "No email"}
            </Typography>
          </Box>
        </Box>
      ),
    },
    {
      id: "tracker",
      label: "Journey",
      renderData: (row) => (
        <Box sx={{ minWidth: 0, maxWidth: 200 }}>
          <Typography
            sx={{
              fontSize: 13,
              fontWeight: 700,
              color: "#0f172a",
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
            title={row.trackerName || ""}
          >
            {row.trackerName || "—"}
          </Typography>
        </Box>
      ),
    },
    {
      id: "submittedAt",
      label: "Submitted on",
      renderData: (row) => (
        <Box>
          <Typography sx={{ fontSize: 13, fontWeight: 700, color: "#0f172a" }}>
            {row.submittedAt
              ? moment(row.submittedAt).format("D MMM YYYY")
              : "—"}
          </Typography>
          <Typography sx={{ fontSize: 11.5, color: MUTED }}>
            {row.submittedAt
              ? `${moment(row.submittedAt).format("HH:mm")} · ${moment(
                  row.submittedAt
                ).fromNow()}`
              : ""}
          </Typography>
        </Box>
      ),
    },
    {
      id: "review",
      label: "Review",
      renderData: (row) => (
        <ReviewCell status={row.reviewStatus} detail={row.reviewDetail} />
      ),
    },
  ];

  const tableRows = filtered.map((row) => ({
    ...row,
    MENU_OPTIONS,
  }));

  return (
    <>
      <CustomConfirmation
        openDelete={openDelete}
        setOpenDelete={setOpenDelete}
        title="Delete this submission?"
        handleAgree={handleDelete}
      />

      <div className="container-fluid tracker-module-page">
        <div className="row mb-3">
          <div className="col-lg-5 col-sm-12">
            {selectedTracker ? (
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 0.5,
                  mb: 1,
                  flexWrap: "wrap",
                }}
              >
                <IconButton
                  size="small"
                  onClick={() => navigate(`/trackers/${selectedTracker.id}`)}
                  sx={{ p: 0.75 }}
                >
                  <ArrowBackIcon fontSize="small" />
                </IconButton>
                <Typography
                  component="button"
                  type="button"
                  onClick={() => navigate(`/trackers/${selectedTracker.id}`)}
                  sx={{
                    border: 0,
                    background: "none",
                    p: 0,
                    cursor: "pointer",
                    color: MUTED,
                    fontSize: 13,
                    "&:hover": { color: PRIMARY },
                  }}
                >
                  {selectedTracker.name || "Tracker"}
                </Typography>
                <Typography sx={{ color: MUTED, fontSize: 13 }}>/</Typography>
                <Typography
                  sx={{ color: "#0f172a", fontSize: 13, fontWeight: 600 }}
                >
                  Submissions
                </Typography>
              </Box>
            ) : null}
            <h2 style={{ marginBottom: 4 }}>Submissions</h2>
            <Typography sx={{ color: "text.secondary", fontSize: 14 }}>
              {selectedTracker
                ? `Showing entries for ${selectedTracker.name || "this tracker"}.`
                : "Who submitted, on which journey, and what needs a coach review."}
            </Typography>
          </div>
          <div className="col-lg-7 col-sm-12">
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "flex-end",
                gap: 1,
                flexWrap: "wrap",
              }}
            >
              {selectedTracker ? (
                <button
                  type="button"
                  className="small-contained-outlined-button"
                  onClick={clearTrackerFilter}
                >
                  All trackers
                </button>
              ) : null}
              <button
                type="button"
                className="small-contained-outlined-button"
                onClick={exportCsv}
              >
                <FileDownloadOutlinedIcon sx={{ fontSize: 16, mr: 0.5 }} />
                Export CSV
              </button>
            </Box>
          </div>
        </div>

        <Box
          sx={{
            display: "flex",
            flexWrap: "wrap",
            gap: 1.25,
            mb: 2,
          }}
        >
          <StatCard
            label="All submissions"
            value={allStats.total}
            hint={`${filtered.length} matching filters`}
            active={
              reviewFilter === "all" &&
              !search.trim() &&
              sourceFilter === "all" &&
              coachFilter === "all" &&
              statusFilter === "all"
            }
            onClick={clearAllFilters}
            tone="primary"
          />
          <StatCard
            label="People"
            value={allStats.people}
            hint="Unique individuals"
          />
          <StatCard
            label="Reviews due"
            value={allStats.reviewsDue}
            hint="Pending + overdue"
            active={reviewFilter === "due"}
            onClick={() =>
              setFilter("review", reviewFilter === "due" ? "all" : "due")
            }
            tone="warn"
          />
          <StatCard
            label="Overdue"
            value={allStats.overdue}
            hint="Needs coach attention"
            active={reviewFilter === SUBMISSION_REVIEW.OVERDUE}
            onClick={() =>
              setFilter(
                "review",
                reviewFilter === SUBMISSION_REVIEW.OVERDUE
                  ? "all"
                  : SUBMISSION_REVIEW.OVERDUE
              )
            }
            tone="danger"
          />
        </Box>

        <Box
          sx={{
            display: "flex",
            flexWrap: "wrap",
            alignItems: "center",
            gap: 1.25,
            mb: 1.5,
          }}
        >
          <TextField
            select
            size="small"
            label="Tracker"
            value={trackerFilter}
            onChange={(e) => setFilter("tracker", e.target.value)}
            sx={{ ...fieldSx, minWidth: { xs: "100%", sm: 180 }, flex: "1 1 160px" }}
          >
            <MenuItem value="all">All trackers</MenuItem>
            {trackers.map((tracker) => (
              <MenuItem key={tracker.id} value={tracker.id}>
                {tracker.name || "Untitled"}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            select
            size="small"
            label="Status"
            value={statusFilter}
            onChange={(e) => setFilter("status", e.target.value)}
            disabled={trackerFilter === "all"}
            sx={{ ...fieldSx, minWidth: { xs: "100%", sm: 150 }, flex: "1 1 140px" }}
          >
            <MenuItem value="all">
              {trackerFilter === "all"
                ? "Pick a tracker first"
                : "Any status"}
            </MenuItem>
            {statusOptions.map((name) => (
              <MenuItem key={name} value={name}>
                {name}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            select
            size="small"
            label="Review"
            value={reviewFilter}
            onChange={(e) => setFilter("review", e.target.value)}
            sx={{ ...fieldSx, minWidth: { xs: "100%", sm: 150 }, flex: "1 1 140px" }}
          >
            {SUBMISSION_REVIEW_OPTIONS.map((option) => (
              <MenuItem key={option.value} value={option.value}>
                {option.label}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            size="small"
            label="Search"
            placeholder="Person, email, journey…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            sx={{
              ...fieldSx,
              minWidth: { xs: "100%", sm: 200 },
              flex: "1.4 1 200px",
            }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={{ fontSize: 18, color: MUTED }} />
                </InputAdornment>
              ),
            }}
          />
          <button
            type="button"
            className="small-contained-button"
            onClick={openFilterDrawer}
            style={{
              height: 40,
              position: "relative",
              display: "inline-flex",
              alignItems: "center",
              gap: 8,
              flexShrink: 0,
            }}
          >
            Filters
            <Icon width={19} height={19} icon={roundFilterList} />
            {moreFilterCount > 0 ? (
              <span
                style={{
                  position: "absolute",
                  bottom: "59%",
                  right: "8%",
                }}
              >
                <div className="dot-filter-count">{moreFilterCount}</div>
              </span>
            ) : null}
          </button>
        </Box>

        <CustomDrawer
          isOpenDrawer={filterDrawerOpen}
          onOpenDrawer={openFilterDrawer}
          onCloseDrawer={closeFilterDrawer}
          pageTitle="Filters"
          componentToPassDown={
            <div className="container new-memories px-4">
              <TextField
                select
                fullWidth
                size="small"
                label="Source"
                value={drawerFilters.source}
                onChange={(e) =>
                  setDrawerFilters((prev) => ({
                    ...prev,
                    source: e.target.value,
                  }))
                }
                className="mt-3"
                sx={fieldSx}
              >
                {SUBMISSION_SOURCE_OPTIONS.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </TextField>
              <TextField
                select
                fullWidth
                size="small"
                label="Tag"
                value={drawerFilters.tag}
                onChange={(e) =>
                  setDrawerFilters((prev) => ({
                    ...prev,
                    tag: e.target.value,
                  }))
                }
                className="mt-3"
                sx={fieldSx}
              >
                <MenuItem value="all">Any tag</MenuItem>
                {tagOptions.map((name) => (
                  <MenuItem key={name} value={name}>
                    {name}
                  </MenuItem>
                ))}
              </TextField>
              <TextField
                select
                fullWidth
                size="small"
                label="Coach"
                value={drawerFilters.coach}
                onChange={(e) =>
                  setDrawerFilters((prev) => ({
                    ...prev,
                    coach: e.target.value,
                  }))
                }
                className="mt-3"
                sx={fieldSx}
              >
                <MenuItem value="all">Any coach</MenuItem>
                {coachOptions.map((name) => (
                  <MenuItem key={name} value={name}>
                    {name}
                  </MenuItem>
                ))}
              </TextField>
              <Box sx={{ py: 2 }}>
                <Button
                  fullWidth
                  size="large"
                  color="inherit"
                  variant="outlined"
                  onClick={applyDrawerFilters}
                  startIcon={
                    <svg
                      width="14"
                      height="14"
                      viewBox="0 0 14 14"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                      style={{ marginRight: 5 }}
                    >
                      <path
                        d="M13.1165 0.25H0.883548C0.321452 0.25 0.0378205 0.932013 0.436097 1.33029L5.3125 6.20743V11.6406C5.3125 11.8471 5.41325 12.0406 5.58242 12.1591L7.69179 13.6351C8.10794 13.9264 8.6875 13.6312 8.6875 13.1167V6.20743L13.564 1.33029C13.9615 0.932804 13.6798 0.25 13.1165 0.25Z"
                        fill={get_root_value("--portal-theme-primary")}
                      />
                    </svg>
                  }
                >
                  Filter
                </Button>
              </Box>
              <Button
                fullWidth
                size="large"
                color="inherit"
                variant="outlined"
                className="mb-3"
                onClick={clearDrawerFilters}
                startIcon={<Icon icon={roundClearAll} />}
              >
                Clear All
              </Button>
            </div>
          }
        />

        {activeFilterChips.length > 0 ? (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              flexWrap: "wrap",
              gap: 0.75,
              mb: 2,
            }}
          >
            <Typography sx={{ fontSize: 12.5, color: MUTED, mr: 0.25 }}>
              Filters:
            </Typography>
            {activeFilterChips.map((chip) => (
              <Chip
                key={chip.key}
                size="small"
                label={chip.label}
                onDelete={chip.onClear}
                deleteIcon={<ClearIcon sx={{ fontSize: "16px !important" }} />}
                sx={{
                  height: 26,
                  fontWeight: 600,
                  bgcolor: PRIMARY_SOFT,
                  color: PRIMARY,
                  "& .MuiChip-deleteIcon": { color: PRIMARY },
                }}
              />
            ))}
            <button
              type="button"
              className="small-contained-outlined-button"
              onClick={clearAllFilters}
              style={{ height: 26, padding: "0 10px", fontSize: 12 }}
            >
              Clear all
            </button>
            <Typography sx={{ fontSize: 12.5, color: MUTED, ml: "auto" }}>
              {filtered.length} result{filtered.length === 1 ? "" : "s"}
            </Typography>
          </Box>
        ) : (
          <Typography sx={{ fontSize: 12.5, color: MUTED, mb: 2 }}>
            {filtered.length} submission{filtered.length === 1 ? "" : "s"}
          </Typography>
        )}

        {loading ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
            <CircularProgress
              size={36}
              sx={{ color: "var(--portal-theme-primary, #04abbb)" }}
            />
          </Box>
        ) : (
          <CustomMUITable
            TABLE_HEAD={TABLE_HEAD}
            data={tableRows}
            MENU_OPTIONS={MENU_OPTIONS}
            hide_search
            className="card-with-background tracker-module-table"
          />
        )}
      </div>
    </>
  );
}
